<template>
  <footer
    v-once
    :class="classes"
    data-track-id="main-footer"
    :data-track-property="footerVersion"
  >
    <EWave class="main-footer__wave" aria-hidden="true" />
    <div class="main-footer__trees-container">
      <div v-if="showCards && !cardsDisabled" class="main-footer__card-container">
        <component
          :is="`e-${card}-card`"
          v-for="(card, index) in cards"
          :key="card"
          :position="index"
          :data-test-id="`main-footer-${card}-card`"
        />
      </div>
      <img
        class="main-footer__background main-footer__background--light"
        :src="footerGrove"
        loading="lazy"
        aria-hidden="true"
      >
      <img
        class="main-footer__background main-footer__background--dark"
        :src="footerGroveDark"
        loading="lazy"
        aria-hidden="true"
      >
    </div>
    <div class="main-footer__content">
      <nav aria-label="Support Links" class="main-footer__menu">
        <ul class="main-footer__menu-list">
          <li
            v-for="item in menu"
            :key="item.key"
            :class="menuItemClass(item.key)"
          >
            <ELink
              :href="item.url"
              color="secondary"
              data-track-id="footer"
              class="main-footer__link"
              rel="noopener"
              :aria-label="item.label"
              :title="item.name"
              data-test-id="main-footer-link"
            >
              {{ menuItem(item.key) }}
            </ELink>
          </li>
        </ul>
      </nav>
      <div class="main-footer__secondary-menu">
        <nav aria-label="Social Media Links" class="main-footer__menu main-footer__menu-social">
          <ul class="main-footer__menu-list">
            <li
              v-for="item in socialMenu"
              :key="item.key"
              class="main-footer__menu-item"
            >
              <a
                :href="item.url"
                data-track-id="footer"
                class="main-footer__link"
                rel="noopener"
                target="_blank"
                :aria-label="item.label"
                data-test-id="main-footer-social-link"
              >
                <EIcon :icon="item.label" size="m" class="main-footer__social-icon" />
              </a>
            </li>
          </ul>
        </nav>
        <EButton
          variant="outline"
          as="a"
          size="s"
          :href="feedbackUrl"
          class="main-footer__feedback-button"
        >
          {{ $t('common.footer.feedback') }}
        </EButton>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import { MAIN_FOOTER_FAQ_CARDS } from '~~/common/js/constants/feature-flags';
import { getFooterMenu, getSocialMenu as socialMenu, getFeedbackLink as feedbackUrl } from '~~/common/js/universal/menus';
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import EIcon from '~~/common/vue/components/icon';
import EButton from '~~/common/vue/components/button';
import ELink from '~~/common/vue/components/link';
import EFooterGrove from '~~/common/vue/assets/images/footer-grove.as-path.svg';
import EFooterGroveDark from '~~/common/vue/assets/images/footer-grove-dark.as-path.svg';
import EWave from './wave-color.svg';

export default {
  name: 'EMainFooter',
  components: {
    EIcon,
    EButton,
    ELink,
    EWave,
    EBlogCard: () => import('./blog-card'),
    EFaqMoneyCard: () => import('./faq-money-card'),
    EFaqTreesCard: () => import('./faq-trees-card'),
    EFaqProfitsCard: () => import('./faq-profits-card'),
  },
  mixins: [classes],
  props: {
    settingsLink: {
      type: Boolean,
      default: true,
    },
    showCards: {
      type: Boolean,
      default: true,
    },
  },
  fetch() {
    if (!this.showCards || this.cardsDisabled) {
      this.footerVersion = 'empty';
      return;
    }

    if (this.faqCardsEnabled) {
      this.cards = ['faq-money', 'faq-trees', 'faq-profits'];
      this.footerVersion = 'faq_cards';
    } else {
      this.cards = ['blog'];
      this.footerVersion = 'blog_card';
    }

    if (this.cards.length > 1) {
      this.cards = [...this.cards].sort(() => Math.random() - 0.5);
    }
  },
  data() {
    return {
      cards: [],
      footerVersion: '',
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapGetters('goodall', ['getExperiment']),
    menu() {
      return getFooterMenu(this.locale, this.settingsLink);
    },
    footerGrove() {
      return EFooterGrove;
    },
    footerGroveDark() {
      return EFooterGroveDark;
    },
    footerExperimentFlag() {
      return this.getExperiment(MAIN_FOOTER_FAQ_CARDS.flag);
    },
    faqCardsEnabled() {
      return this.footerExperimentFlag?.includes(MAIN_FOOTER_FAQ_CARDS.enabled);
    },
    cardsDisabled() {
      return this.footerExperimentFlag?.includes(MAIN_FOOTER_FAQ_CARDS.disabled);
    },
    socialMenu,
    feedbackUrl,
  },
  methods: {
    menuItemClass(key) {
      const baseClass = 'main-footer__menu-item';
      return [baseClass, getModifierClass(baseClass, key)];
    },
    menuItem(key) {
      // @TODO refactor when rebrand is ready
      // Change faq to help in translation keys
      return key === 'help' ? this.$t('common.footer.faq') : this.$t(`common.footer.${key}`);
    },
  },
};
</script>

<style lang="scss" scoped>
$social-menu-spacing: 10px;
$social-icon-size-mobile: 30px;

.main-footer__menu-item {
  margin-bottom: $space-s;
  padding: 0 $space-s;

  @include tablet {
    &:first-of-type {
      padding-left: 0;
    }

    &:last-of-type {
      padding-right: 0;
    }
  }

  @include desktop {
    margin-bottom: 0;
  }
}

.main-footer {
  position: relative;
  font-size: $font-m;
  color: var(--color-text-secondary);
  width: 100%;
  padding-top: $space-m;
  background: var(--color-background-primary);

  @include desktop {
    padding-top: $space-2l;
  }
}

.main-footer__content {
  text-align: center;
  width: 100%;
  background-color: var(--color-background-quaternary);
  padding: $space-1l $space-4l;

  @include desktop {
    padding: $space-1s $space-1l;
    display: flex;
    justify-content: space-between;
    margin: 0;
  }
}

.main-footer__link {
  transition: color $timing-2s $easing;
  white-space: nowrap;

  &:hover,
  &:focus {
    color: var(--color-link-secondary);
  }

  &:first-of-type {
    padding-left: 0;
  }
}

.main-footer__wave {
  width: 100%;
  height: $space-5l;
  color: var(--color-background-secondary); // SVG background
}

.main-footer__background {
  width: max-content;
  background-color: var(--color-background-secondary); // SVG background
}

.main-footer__background--dark {
  display: none;
}

.main-footer__menu {
  display: flex;
  align-items: center;
}

.main-footer__menu-list {
  margin: 0;
  list-style-type: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 0;
  width: 100%;
}

.main-footer__menu-social {
  display: inline-block;
  width: 100%;

  @include desktop {
    width: auto;
  }

  .main-footer__menu-item {
    display: inline-block;
    margin-bottom: 0;
    padding: 0;
    height: $social-icon-size-mobile;
    width: $social-icon-size-mobile;

    @include desktop {
      height: unset;
      width: unset;
    }

    & + .main-footer__menu-item {
      margin-left: $space-m;

      @include desktop {
        margin-left: $social-menu-spacing;
      }
    }
  }

  // For A11Y, we need to set the dimension, see: https://ecosia.atlassian.net/browse/FE-371
  .main-footer__link {
    display: inline-block;
    width: $social-icon-size-mobile;
    height: $social-icon-size-mobile;

    @include desktop {
      width: $icon-size-m;
      height: $icon-size-m;
    }
  }
}

.main-footer__social-icon {
  color: $c-gray-70;
  height: $social-icon-size-mobile;
  width: $social-icon-size-mobile;

  &:hover {
    color: $c-gray-50;
  }

  @include desktop {
    height: unset;
    width: unset;
  }
}

// TODO: only temporary until DS is revisited,
// see https://ecosia.atlassian.net/browse/CS-463
.dark {
  .main-footer__background--light {
    display: none;
  }

  .main-footer__background--dark {
    display: block;
  }

  .main-footer__social-icon {
    color: $white;

    &:hover {
      color: $c-gray-30;
    }
  }
}

.main-footer__feedback-button {
  margin-top: $space-s;

  @include desktop {
    margin-top: 0;
    margin-left: $social-menu-spacing;
  }
}

.main-footer__trees-container {
  position: relative;
  background: var(--color-background-secondary);
  padding-top: $space-m;
  display: flex;
  flex-direction: column;

  @include desktop {
    padding-top: 0;

    .main-footer__card-container {
      position: absolute;
      bottom: $space-1l;
      left: $space-l;
      margin: 0;
    }
  }
}

.main-footer__card-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;

  @include desktop {
    flex-wrap: unset;
  }
}
</style>
